import { Checkbox, Button, Col, Descriptions, Divider, Drawer, Form, Input, Modal, Row, Select, Space, Tag, Switch, Tooltip, } from "antd";
import { CloseOutlined, DownloadOutlined, EditOutlined, EyeOutlined, LaptopOutlined, TableOutlined, } from "@ant-design/icons";
import { BiFilterAlt } from "react-icons/bi";
import Papa from "papaparse";
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Client } from "../../core/api-client";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import ActivityLogComponent from "../../shared/components/activity-log/activity-log.component";
import { allPermissions } from "../../shared/constants/constants";
import { API_IT_ASSET_EXPORT, API_IT_ASSET_LIST, API_IT_ASSET_OPTIONS, API_IT_ASSET_UPDATE, API_GET_IT_ASSET_TABLE_PREFERENCES, API_SAVE_IT_ASSET_TABLE_PREFERENCES } from "../../shared/routes/private.routes";
import { RootState } from "../../shared/types/common";
import { accessAllowed, showNotification } from "../../shared/utils/utils";
import { current } from "@reduxjs/toolkit";

const PAGE_SIZE = 15;
const IT_ASSET_DEFAULT_VISIBLE_COLUMNS = [
  "entityName",
  "employeeId",
  "staffName",
  "email",
  "employmentModeName",
  "staffStatus",
  "projectName",
  "assetHostName",
  "o365",
  "assetCategory",
  "laptopYear",
  "model",
  "cs",
  "action1",
];
const optionFields = [
  { name: "o365OptionId", label: "O365", type: "O365" },
  { name: "assetCategoryOptionId", label: "Asset Category", type: "ASSET_CATEGORY" },
  { name: "laptopYearOptionId", label: "Laptop Year", type: "LAPTOP_YEAR" },
  { name: "csOptionId", label: "CrowdStrike", type: "CS" },
  { name: "modelOptionId", label: "Model", type: "MODEL" },
  { name: "action1OptionId", label: "Action1", type: "ACTION1" },


];
const filterFields = [
  { name: "entityId", label: "Entity", type: "ENTITY" },
  { name: "employmentModeId", label: "Employment Mode", type: "EMPLOYMENT_MODE" },
  { name: "staffStatus", label: "Status", type: "STAFF_STATUS" },
  { name: "lineManagerId", label: "Line Manager", type: "LINE_MANAGER" },
  { name: "projectId", label: "Project", type: "PROJECT" },
  { name: "o365OptionId", label: "O365", type: "O365" },
  { name: "assetCategoryOptionId", label: "Asset Category", type: "ASSET_CATEGORY" },
  { name: "csOptionId", label: "CrowdStrike", type: "CS" },
  { name: "action1OptionId", label: "Action1", type: "ACTION1" },
  { name: "modelOptionId", label: "Model", type: "MODEL" },
  { name: "laptopYearOptionId", label: "Laptop Year", type: "LAPTOP_YEAR" },
];

const IT_TABLE_NOWRAP_STYLE: CSSProperties = {
  whiteSpace: "nowrap",
  wordBreak: "keep-all",
  width: "max-content",
  minWidth: "max-content",
};
const IT_TABLE_NOWRAP_CONTENT_STYLE: CSSProperties = {
  ...IT_TABLE_NOWRAP_STYLE,
  display: "inline-block",
};
const withItNoWrapColumns = (columns: any[]) => columns.map((column) => {
  const skipContentWrap = ["actions", "staffStatus"].includes(column.key);
  return {
    ...column,
    title: typeof column.title === "string"
      ? <span style={IT_TABLE_NOWRAP_CONTENT_STYLE}>{column.title}</span>
      : column.title,
    render: skipContentWrap
      ? column.render
      : (...args: any[]) => <span style={IT_TABLE_NOWRAP_CONTENT_STYLE}>{column.render ? column.render(...args) : args[0]}</span>,
    onCell: (...args: any[]) => {
      const cellProps = column.onCell?.(...args) || {};
      return { ...cellProps, style: { ...cellProps.style, ...IT_TABLE_NOWRAP_STYLE } };
    },
    onHeaderCell: (...args: any[]) => {
      const headerProps = column.onHeaderCell?.(...args) || {};
      return { ...headerProps, style: { ...headerProps.style, ...IT_TABLE_NOWRAP_STYLE } };
    },
  };
});

const downloadCsv = (rows: any[]) => {
  const csv = Papa.unparse(rows.map((row) => ({
    Entity: row.entityName, "Employee ID": row.employeeId, "Employment Mode": row.employmentModeName,
    "Staff Name": row.staffName, "Staff Email ID": row.email, Status: row.staffStatus, "Line Manager": row.lineManagerName,
    Project: row.projectName, "Asset Host Name": row.assetHostName, O365: row.o365,
    "Asset Category": row.assetCategory, "Laptop Year": row.laptopYear, Model: row.model,
    CrowdStrike: row.cs, Action1: row.action1,
  })), { escapeFormulae: true });
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url; link.download = "manage-it-assets.csv"; link.click(); URL.revokeObjectURL(url);
};

const ManageItAssetsPage = () => {
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const [form] = Form.useForm();
  const [filterForm] = Form.useForm();
  const [optionForm] = Form.useForm();
  const [addingOption, setAddingOption] = useState<(typeof optionFields)[number] | null>(null);
  const [creatingOption, setCreatingOption] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [options, setOptions] = useState<Record<string, Array<{ id: string; value: string }>>>({});
  const [filters, setFilters] = useState<any>({});
  const [filterVisible, setFilterVisible] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [logRecord, setLogRecord] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [sorter, setSorter] = useState({ sort_by: "", sort_type: "DESC" });
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    IT_ASSET_DEFAULT_VISIBLE_COLUMNS
  );
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [preferencesCanSave, setPreferencesCanSave] = useState(false);
  const skipNextPreferenceSave = useRef(true);

  const canUpdate = accessAllowed(myPermissions, allPermissions.UPDATE_IT_ASSET);
  const canExport = accessAllowed(myPermissions, allPermissions.EXPORT_IT_ASSET);

  const load = useCallback(async (nextPage = page) => {
    setLoading(true);
    try {
      const response = await Client.post(API_IT_ASSET_LIST, filters, {
        params: {
          page: nextPage,
          limit: pageSize,
          sort_by: sorter.sort_by || "",
          sort_type: sorter.sort_type || "DESC",
        },
      });
      setRows(response?.data?.data || []); setTotal(Number(response?.data?.total) || 0); setPage(nextPage);
    } catch { showNotification("Unable to load IT asset records", "error"); }
    finally { setLoading(false); }
  }, [filters, page, pageSize, sorter]);

  useEffect(() => { load(1); }, [filters, pageSize, sorter]);
  useEffect(() => {
    Client.get(API_IT_ASSET_OPTIONS).then((response) => setOptions(response?.data?.data || {}))
      .catch(() => showNotification("Unable to load IT asset options", "error"));
  }, []);
  // Load saved IT Assets table preferences
  useEffect(() => {
    let active = true;

    const loadPreferences = async () => {
      try {
        const response = await Client.get(
          API_GET_IT_ASSET_TABLE_PREFERENCES
        );

        if (!active) return;

        const savedColumns = response?.data?.data?.visibleColumns;

        if (Array.isArray(savedColumns)) {
          const validColumns = savedColumns.filter(
            (key: string) =>
              IT_ASSET_DEFAULT_VISIBLE_COLUMNS.includes(key)
          );

          setVisibleColumns(
            validColumns.length > 0
              ? validColumns
              : [...IT_ASSET_DEFAULT_VISIBLE_COLUMNS]
          );
        } else {
          setVisibleColumns([...IT_ASSET_DEFAULT_VISIBLE_COLUMNS]);
        }
      } catch {
        if (!active) return;

        setVisibleColumns([...IT_ASSET_DEFAULT_VISIBLE_COLUMNS]);
        showNotification(
          "Unable to load IT Assets table preferences",
          "error"
        );
      } finally {
        if (active) {
          setPreferencesLoaded(true);
          setPreferencesCanSave(true);
        }
      }
    };

    loadPreferences();

    return () => {
      active = false;
    };
  }, []);

  // Automatically save column visibility
  useEffect(() => {
    if (!preferencesLoaded || !preferencesCanSave) return;

    if (skipNextPreferenceSave.current) {
      skipNextPreferenceSave.current = false;
      return;
    }

    const savePreferences = async () => {
      try {
        await Client.put(API_SAVE_IT_ASSET_TABLE_PREFERENCES, {
          preferences: { visibleColumns },
        });
      } catch {
        showNotification(
          "Unable to save IT Assets table preferences",
          "error"
        );
      }
    };

    savePreferences();
  }, [visibleColumns, preferencesLoaded, preferencesCanSave]);

  const [originalValues, setOriginalValues] = useState<any>({});

  const openEdit = (record: any) => {
    setEditing(record);
    const values = {
      assetHostName: record.assetHostName,
      o365OptionId: record.o365OptionId,
      assetCategoryOptionId: record.assetCategoryOptionId,
      laptopYearOptionId: record.laptopYearOptionId,
      modelOptionId: record.modelOptionId,
      csOptionId: record.csOptionId,
      action1OptionId: record.action1OptionId,
    };

    setOriginalValues(values);
    form.setFieldsValue(values);
  };
  const save = async (values: any) => {
    setSaving(true);

    try {
      const payload: any = {};

      // Host name மாற்றப்பட்டிருந்தால் மட்டும் சேர்க்கவும்
      if (values.assetHostName !== originalValues.assetHostName) {
        payload.assetHostName = values.assetHostName ?? null;
      }

      // Option fields: மாற்றப்பட்டவை மட்டும் அனுப்பவும்
      optionFields.forEach((field) => {
        const oldValue = originalValues[field.name] ?? null;
        const newValue = values[field.name] ?? null;

        if (oldValue !== newValue) {
          payload[field.name] = newValue;
        }
      });

      console.log("Update payload:", payload);

      await Client.put(API_IT_ASSET_UPDATE + editing.staffId, payload);

      showNotification("IT asset details updated successfully", "success");
      setEditing(null);
      load(1);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message;

      showNotification(
        Array.isArray(message)
          ? message.join(", ")
          : message || "Unable to update IT asset details",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };
  const exportRows = async () => {

    try {
      const response = await Client.post(API_IT_ASSET_LIST, filters, {
        params: {
          sort_by: sorter.sort_by || "",
          sort_type: sorter.sort_type || "DESC",
        },
      });
      downloadCsv(response?.data?.data || []);
    }
    catch { showNotification("Unable to export IT asset records", "error"); }
  };

  const createOption = async ({ value }: { value: string }) => {
    if (!addingOption || creatingOption) return;
    setCreatingOption(true);
    try {
      const response = await Client.post(API_IT_ASSET_OPTIONS, {
        optionType: addingOption.type, value: value.trim(),
      });
      const option = response?.data?.data;
      if (!option?.id) throw new Error("Unable to add option");
      setOptions((current) => ({
        ...current,
        [addingOption.type]: [...(current[addingOption.type] || []).filter((item) => item.id !== option.id), option]
          .sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true, sensitivity: "base" })),
      }));
      form.setFieldsValue({ [addingOption.name]: option.id });
      setAddingOption(null);
      optionForm.resetFields();
      showNotification("Option added and selected", "success");
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || "Unable to add option";
      showNotification(Array.isArray(message) ? message.join(", ") : message, "error");
    } finally { setCreatingOption(false); }
  };

  const getSortOrder = (columnKey: string) => {
    if (sorter.sort_by !== columnKey) return null;
    return sorter.sort_type === "ASC" ? "ascend" : "descend";
  };

  const getSortableColumnProps = (columnKey: string) => ({
    sorter: true,
    sortOrder: getSortOrder(columnKey),
  });

  const onChangeHandler = (_pagination: any, _filters: any, tableSorter: any) => {
    const activeSorter = Array.isArray(tableSorter)
      ? tableSorter.find((item) => item?.order)
      : tableSorter;

    if (!activeSorter?.order) {
      setSorter({ sort_by: "", sort_type: "DESC" });
      return;
    }

    setSorter({
      sort_by: activeSorter.columnKey || "",
      sort_type: activeSorter.order === "ascend" ? "ASC" : "DESC",
    });
  };

  const noWrapCell = { style: { whiteSpace: "nowrap" as const } };
  const columns = useMemo(() => withItNoWrapColumns([
    { title: "Entity", dataIndex: "entityName", key: "entityName", ...getSortableColumnProps("entityName"), width: 140, onHeaderCell: () => noWrapCell, onCell: () => noWrapCell },
    { title: "Employee ID", dataIndex: "employeeId", key: "employeeId", ...getSortableColumnProps("employeeId"), width: 140, onHeaderCell: () => noWrapCell, onCell: () => noWrapCell },
    { title: "Staff Name", dataIndex: "staffName", key: "staffName", ...getSortableColumnProps("staffName"), width: 170, onHeaderCell: () => noWrapCell, onCell: () => noWrapCell },
    { title: "Staff Email ID", dataIndex: "email", key: "email", ...getSortableColumnProps("email"), width: 240, onHeaderCell: () => noWrapCell, onCell: () => noWrapCell },
    { title: "Employment Mode", dataIndex: "employmentModeName", key: "employmentModeName", ...getSortableColumnProps("employmentModeName"), width: 180, onHeaderCell: () => noWrapCell, onCell: () => noWrapCell },
    {
      title: "Status", dataIndex: "staffStatus", key: "staffStatus", ...getSortableColumnProps("staffStatus"), width: 140, onHeaderCell: () => noWrapCell, onCell: () => noWrapCell,
      render: (status: string) => {
        const color = status === "ACTIVE" ? "success" : status === "DRAFT" ? "processing" : status === "NOTICE PERIOD" ? "warning" : "red";
        return status ? <Tag color={color}>{status}</Tag> : "—";
      }
    },
    { title: "Project", dataIndex: "projectName", key: "projectName", ...getSortableColumnProps("projectName"), width: 180, onHeaderCell: () => noWrapCell, onCell: () => noWrapCell },
    { title: "Asset Host Name", dataIndex: "assetHostName", key: "assetHostName", ...getSortableColumnProps("assetHostName"), width: 180, onHeaderCell: () => noWrapCell, onCell: () => noWrapCell },
    { title: "O365", dataIndex: "o365", key: "o365", ...getSortableColumnProps("o365"), width: 140, onHeaderCell: () => noWrapCell, onCell: () => noWrapCell },
    { title: "Asset Category", dataIndex: "assetCategory", key: "assetCategory", ...getSortableColumnProps("assetCategory"), width: 180, onHeaderCell: () => noWrapCell, onCell: () => noWrapCell },
    { title: "Laptop Year", dataIndex: "laptopYear", key: "laptopYear", ...getSortableColumnProps("laptopYear"), width: 150, onHeaderCell: () => noWrapCell, onCell: () => noWrapCell },
    { title: "Model", dataIndex: "model", key: "model", ...getSortableColumnProps("model"), width: 180, onHeaderCell: () => noWrapCell, onCell: () => noWrapCell },
    { title: "CrowdStrike", dataIndex: "cs", key: "cs", ...getSortableColumnProps("cs"), width: 150, onHeaderCell: () => noWrapCell, onCell: () => noWrapCell },
    { title: "Action1", dataIndex: "action1", key: "action1", ...getSortableColumnProps("action1"), width: 140, onHeaderCell: () => noWrapCell, onCell: () => noWrapCell },
    {
      title: "Actions", key: "actions", fixed: "right" as const, width: 110,
      onHeaderCell: () => noWrapCell, onCell: () => noWrapCell,
      render: (_: any, record: any) => <Space size={4}>
        {canUpdate && <Button type="text" icon={<EditOutlined />} title="Edit IT details" aria-label="Edit IT details" onClick={() => openEdit(record)} />}
        {record.assetId && <Button type="text" icon={<EyeOutlined />} title="View activity log" aria-label="View activity log" onClick={() => setLogRecord(record)} />}
      </Space>
    },
  ]), [canUpdate, sorter]);
  const displayedColumns = useMemo(
    () =>
      columns.filter(
        (column: any) =>
          column.key === "actions" ||
          visibleColumns.includes(String(column.key))
      ),
    [columns, visibleColumns]
  );

  return <div className="app-page">
    <div className="app-page-header"><Row align="middle" justify="space-between" wrap>
      <Col flex="1"><div className="app-page-title-wrap"><div className="app-page-icon"><LaptopOutlined /></div><h1 className="app-page-title">Manage IT</h1></div>
        <p className="app-page-subtitle">Manage staff IT and asset information</p></Col>
      <Col><Space wrap><Checkbox className="app-include-inactive" checked={!!filters.includeInactive}
        onChange={(event) => setFilters((previous: any) => ({ ...previous, includeInactive: event.target.checked }))}>Include Inactive</Checkbox>{canExport && <Button icon={<DownloadOutlined />} onClick={() => exportRows()}>Export</Button>}
        <Button type="primary" shape="circle" icon={<BiFilterAlt />} title={filterVisible ? "Hide filters" : "Show filters"} onClick={() => setFilterVisible((value) => !value)} /><Tooltip title="Customize Table">
          <Button
            type="primary"
            shape="circle"
            icon={<TableOutlined />}
            aria-label="Customize Table"
            onClick={() => setColumnSettingsOpen(true)}
          />
        </Tooltip></Space></Col>
    </Row></div>
    {filterVisible && <div className="app-filter-bar">
      <div className="app-filter-header"><div className="app-filter-title"><BiFilterAlt style={{ fontSize: 14 }} />Filters</div>
        <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => setFilterVisible(false)} title="Close filters" aria-label="Close filters" />
      </div>
      <div className="table-filter-form"><Form form={filterForm} onFinish={(values) => setFilters((previous: any) => ({ ...values, includeInactive: previous.includeInactive }))}>
        <Row gutter={12}><Col xs={12} sm={8} md={6} lg={4}><Form.Item name="search"><Input allowClear placeholder="Employee ID or staff name" /></Form.Item></Col>
          {filterFields.map((field) => <Col xs={12} sm={8} md={6} lg={4} key={field.name}><Form.Item name={field.name}><Select mode="multiple" allowClear showSearch optionFilterProp="label" placeholder={field.label}
            options={(options[field.type] || []).map((item) => ({ value: item.id, label: item.value }))} /></Form.Item></Col>)}</Row>
        <Space wrap><Button onClick={() => { filterForm.resetFields(); setFilters((previous: any) => ({ includeInactive: previous.includeInactive })); }}>Clear</Button><Button type="primary" htmlType="submit">Apply</Button></Space>
      </Form></div>
    </div>}
    {/* IT Assets Table */}
    <div className="app-table-card milestones-table-card">
      <DataTableComponent
        rowKey="staffId"
        pagination
        loading={loading}
        dataSource={rows}
        columns={displayedColumns}
        tableWidth="max-content"
        defaultPage={1}
        currentPage={page}
        totalRows={total}
        pageSize={pageSize}
        onChange={onChangeHandler}
        onPageChange={load}
        onPageSizeChange={(_, size) => {
          setPageSize(size);
          setPage(1);
        }}
      />
    </div>

    {/* Customize Table View Modal */}
    <Modal
      title="Customize Table View"
      open={columnSettingsOpen}
      onCancel={() => setColumnSettingsOpen(false)}
      footer={[
        <Button
          key="reset"
          onClick={() =>
            setVisibleColumns([...IT_ASSET_DEFAULT_VISIBLE_COLUMNS])
          }
        >
          Reset to Default
        </Button>,
        <Button
          key="done"
          type="primary"
          onClick={() => setColumnSettingsOpen(false)}
        >
          Done
        </Button>,
      ]}
      destroyOnHidden
    >
      <p>
        Select which columns you want to display in the IT Assets table.
      </p>

      <div
        style={{
          maxHeight: "calc(100vh - 340px)",
          overflowY: "auto",
          overflowX: "hidden",
          paddingRight: 8,
        }}
      >
        <Row gutter={[32, 0]}>
          {columns
            .filter((column: any) => column.key !== "actions")
            .map((column: any) => {
              const key = String(column.key);
              const checked = visibleColumns.includes(key);
              const isLastVisible =
                visibleColumns.length === 1 && checked;

              return (
                <Col span={12} key={key}>
                  <div
                    style={{
                      minHeight: 60,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      borderBottom: "1px solid #e8eaf0",
                      padding: "12px 0",
                    }}
                  >
                    <span>{column.title}</span>

                    <Switch
                      size="small"
                      checked={checked}
                      disabled={isLastVisible}
                      onChange={(enabled) => {
                        setVisibleColumns((previous) => {
                          if (enabled) {
                            return previous.includes(key)
                              ? previous
                              : [...previous, key];
                          }

                          if (previous.length <= 1) {
                            return previous;
                          }

                          return previous.filter(
                            (columnKey) => columnKey !== key
                          );
                        });
                      }}
                    />
                  </div>
                </Col>
              );
            })}
        </Row>
      </div>
    </Modal>
    <Modal title={<div className="it-asset-modal-title"><span className="it-asset-modal-title-icon"><LaptopOutlined /></span><span>Edit IT Details</span></div>}
      open={!!editing} width={1100} centered maskClosable={false} closable={!saving}
      className="it-asset-edit-modal"
      onCancel={() => setEditing(null)} destroyOnHidden
      footer={<Space><Button disabled={saving} onClick={() => setEditing(null)}>Cancel</Button><Button type="primary" loading={saving} onClick={() => form.submit()}>Save</Button></Space>}>
      {editing && <>
        <Descriptions title="Staff Details" size="small" column={{ xs: 1, sm: 2, md: 4 }} layout="vertical">
          <Descriptions.Item label="Employee ID">{editing.employeeId || "-"}</Descriptions.Item>
          <Descriptions.Item label="Staff Name">{editing.staffName || "-"}</Descriptions.Item>
          <Descriptions.Item label="Staff Email ID">{editing.email || "-"}</Descriptions.Item>
          <Descriptions.Item label="Entity">{editing.entityName || "-"}</Descriptions.Item>
          <Descriptions.Item label="Employment Mode">{editing.employmentModeName || "-"}</Descriptions.Item>
          <Descriptions.Item label="Status">{editing.staffStatus || "-"}</Descriptions.Item>
          <Descriptions.Item label="Line Manager">{editing.lineManagerName || "-"}</Descriptions.Item>
          <Descriptions.Item label="Project">{editing.projectName || "-"}</Descriptions.Item>
        </Descriptions>
        <Divider />
        <Form form={form} layout="vertical" onFinish={save} disabled={saving}>
          <h3 style={{ marginBottom: 16 }}>IT Asset Details</h3>
          <Row gutter={16}>
            <Col xs={24} sm={12}><Form.Item name="assetHostName" label="Asset Host Name" rules={[{ max: 100 }]}><Input maxLength={100} /></Form.Item></Col>
            {optionFields.map((field) => <Col xs={24} sm={12} key={field.name}><Form.Item name={field.name} label={field.label}><Select allowClear showSearch optionFilterProp="label"
              dropdownRender={(menu) => <>
                {menu}
                <Divider style={{ margin: "8px 0" }} />
                <Button type="text" block disabled={!canUpdate || saving}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => { optionForm.resetFields(); setAddingOption(field); }}>
                  + Add new {field.label}
                </Button>
              </>}
              options={(options[field.type] || []).map((item) => ({ value: item.id, label: item.value }))} /></Form.Item></Col>)}
          </Row>
        </Form>
      </>}
    </Modal>
    <Modal title={`Add ${addingOption?.label || "option"}`} open={!!addingOption}
      onCancel={() => { if (!creatingOption) setAddingOption(null); }}
      onOk={() => optionForm.submit()} okText="Add and select" confirmLoading={creatingOption}
      closable={!creatingOption} cancelButtonProps={{ disabled: creatingOption }} maskClosable={false}>
      <Form form={optionForm} layout="vertical" onFinish={createOption}>
        <Form.Item name="value" label={addingOption?.label}
          rules={[{ required: true, whitespace: true, message: "Enter a value" }, { max: 150 }]}>
          <Input maxLength={150} disabled={creatingOption} />
        </Form.Item>
        <p>The new option will be available for all staff. Save IT Details to assign it to this staff member.</p>
      </Form>
    </Modal>
    <Drawer title="Log Details" open={!!logRecord} onClose={() => setLogRecord(null)}>
      {logRecord?.assetId && <ActivityLogComponent id={logRecord.assetId} record={logRecord} module="IT Asset" />}
    </Drawer>
  </div >;
};
export default ManageItAssetsPage;
